import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUser } from "@/features/auth/hooks/use-user";
import {
  useCreateMember,
  useRemoveAccess,
  useSetAccessMember,
  useUpdateAccessRole,
} from "@/features/clients/hooks/use-clients";
import { useHasRole } from "@/features/clients/hooks/use-has-role";
import type {
  ClientAccessWithUser,
  ClientMemberWithUser,
  ClientRole,
} from "@/features/clients/types";
import { useTranslation } from "@/features/utils/i18n";
import {
  CrownIcon,
  DotsThreeOutlineIcon,
  Shield,
  TrashIcon,
  UserPlusIcon,
} from "@phosphor-icons/react";
import type { ColumnDef } from "@tanstack/react-table";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

/** Radix cannot hold an empty string as an item value; "nobody" is a real answer. */
const NOBODY = "__nobody__";

/** The name to put on the team for a login that has none: theirs. */
const accountName = (access: ClientAccessWithUser) => {
  const full = access.user?.full_name?.trim();
  if (full) return full;

  const email = access.user?.email || access.access.email || "";
  // "marie.dupont@salon.fr" is a name the agent can read out loud once the dots
  // are gone; the domain never is.
  const local = email
    .split("@")[0]
    ?.replace(/[._-]+/g, " ")
    .trim();
  return local || email;
};

/**
 * Who this login is, on the team. The one place the two objects meet.
 *
 * A user attached to nobody administers the tenant without being part of what
 * the agent talks about — an accountant, an integrator — which is what
 * `hidden_from_agents` used to say with a flag.
 */
const MemberCell = ({
  access,
  members,
}: {
  access: ClientAccessWithUser;
  members: ClientMemberWithUser[];
}) => {
  const { t } = useTranslation("members");
  const { company: clientId } = useParams<{ company: string }>();
  const setAccessMember = useSetAccessMember();
  const createMember = useCreateMember();
  const isOwner = useHasRole("OWNER");

  const current = access.access.member_id || "";

  if (!isOwner) {
    const member = members.find((m) => m.membership.id === current);
    return (
      <span className="text-sm">
        {member?.membership.display_name || t("status.nobody")}
      </span>
    );
  }

  const handleChange = async (value: string) => {
    if (!clientId) return;
    try {
      await setAccessMember.mutateAsync({
        clientId,
        accessId: access.access.id,
        memberId: value === NOBODY ? "" : value,
      });
      toast.success(t("messages.memberAttached"));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("messages.attachError"),
      );
    }
  };

  /**
   * Put this login on the team, in one click.
   *
   * The select can only offer members that already exist, so attaching somebody
   * who has an account but no member row meant leaving the page, adding a
   * member by hand on Équipe, coming back and picking them. The member is
   * created from the account's own name and attached immediately.
   */
  const handleAddToTeam = async () => {
    if (!clientId) return;
    try {
      const { member } = await createMember.mutateAsync({
        clientId,
        name: accountName(access),
      });
      await setAccessMember.mutateAsync({
        clientId,
        accessId: access.access.id,
        memberId: member.id,
      });
      toast.success(t("messages.memberCreated"));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("messages.createError"),
      );
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Select value={current || NOBODY} onValueChange={handleChange}>
        <SelectTrigger className="h-8 w-[200px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NOBODY}>{t("status.nobody")}</SelectItem>
          {members.map((member) => (
            <SelectItem key={member.membership.id} value={member.membership.id}>
              {member.membership.display_name || member.user?.email}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {!current && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8"
          onClick={handleAddToTeam}
          disabled={createMember.isPending || setAccessMember.isPending}
        >
          <UserPlusIcon className="h-4 w-4" weight="fill" />
          {t("actions.addToTeam")}
        </Button>
      )}
    </div>
  );
};

const AccessAction = ({ access }: { access: ClientAccessWithUser }) => {
  const { t } = useTranslation("members");
  const { company: clientId } = useParams<{ company: string }>();
  const { data: user } = useUser();

  const removeAccess = useRemoveAccess();
  const updateAccessRole = useUpdateAccessRole();
  const isOwner = useHasRole("OWNER");

  if (!isOwner) return null;

  const isPending = access.access.status === "PENDING";
  const isCurrentUser = access.user?.id === user?.id;
  const role = access.access.role;

  const handleUpdateRole = async (newRole: ClientRole) => {
    if (!clientId) return;
    try {
      await updateAccessRole.mutateAsync({
        clientId,
        accessId: access.access.id,
        role: newRole,
      });
      toast.success(t("messages.roleUpdated"));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("messages.roleUpdateError"),
      );
    }
  };

  // The access, by its own id: it exists whether or not an account has
  // accepted it yet, which is why a pending invitation cancels through the
  // very same call.
  const handleRemove = async () => {
    if (!clientId) return;
    try {
      await removeAccess.mutateAsync({ clientId, accessId: access.access.id });
      toast.success(t("messages.removed"));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("messages.removeError"),
      );
    }
  };

  if (isPending) {
    return (
      <div className="text-right">
        <Button
          variant="ghost"
          size="sm"
          onClick={async () => {
            if (!clientId) return;
            try {
              await removeAccess.mutateAsync({
                clientId,
                accessId: access.access.id,
              });
              toast.success(t("messages.invitationCancelled"));
            } catch (error) {
              toast.error(
                error instanceof Error
                  ? error.message
                  : t("messages.invitationCancelError"),
              );
            }
          }}
          className="text-red-600 hover:text-red-700"
        >
          {t("actions.cancel")}
        </Button>
      </div>
    );
  }

  return (
    <div className="text-right">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">{t("common.openMenu")}</span>
            <DotsThreeOutlineIcon weight="fill" className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {role !== "OWNER" && (
            <DropdownMenuItem onClick={() => handleUpdateRole("OWNER")}>
              <CrownIcon className="h-4 w-4" weight="fill" />
              {t("actions.promoteToOwner")}
            </DropdownMenuItem>
          )}
          {role !== "AGENT" && (
            <DropdownMenuItem onClick={() => handleUpdateRole("AGENT")}>
              <Shield className="h-4 w-4" weight="fill" />
              {t("actions.demoteToAgent")}
            </DropdownMenuItem>
          )}
          {role !== "NONE" && (
            <DropdownMenuItem onClick={() => handleUpdateRole("NONE")}>
              <Shield className="h-4 w-4" weight="fill" />
              {t("actions.revokeAccess")}
            </DropdownMenuItem>
          )}
          {!isCurrentUser && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="p-0">
                <Button
                  variant="ghost"
                  onClick={handleRemove}
                  className="text-destructive hover:text-destructive w-full justify-start"
                  confirm={t("messages.removeUserConfirm", {
                    name: access.user?.email || access.access.email || "",
                  })}
                >
                  <TrashIcon className="h-4 w-4" weight="fill" />
                  {t("actions.removeUser")}
                </Button>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export function useAccessColumns(members: ClientMemberWithUser[]) {
  const { t } = useTranslation("members");

  const columns: ColumnDef<ClientAccessWithUser>[] = [
    {
      id: "user",
      header: t("table.account"),
      cell: ({ row }) => {
        const { access, user } = row.original;
        const pending = access.status === "PENDING";

        return (
          <div>
            <div className="font-medium">
              {user?.email || access.email || "—"}
            </div>
            {pending && (
              <div className="text-xs text-muted-foreground">
                {t("status.pending")}
              </div>
            )}
            {!pending && user?.full_name && (
              <div className="text-xs text-muted-foreground">
                {user.full_name}
              </div>
            )}
          </div>
        );
      },
    },
    {
      id: "member",
      header: t("table.isOnTeam"),
      cell: ({ row }) => <MemberCell access={row.original} members={members} />,
    },
    {
      accessorKey: "access.role",
      header: t("table.role"),
      cell: ({ row }) => {
        const role = row.original.access.role;
        const variant =
          role === "OWNER"
            ? "default"
            : role === "NONE"
              ? "destructive"
              : "secondary";

        return (
          <Badge variant={variant}>
            {role === "OWNER" && (
              <CrownIcon weight="fill" className="h-3 w-3 mr-1" />
            )}
            {role}
          </Badge>
        );
      },
    },
    {
      id: "actions",
      header: () => <div className="text-right">{t("table.actions")}</div>,
      cell: ({ row }) => <AccessAction access={row.original} />,
    },
  ];

  return columns;
}
